
import { ChatAgentType } from '@constants/chat-agents';
import type { ChatMessageResponse, SenderType, ForwardedImage } from './chat-message';

/**
 * Per-image upload status tracking
 * Each image in a multi-image message has its own independent status
 */
export interface ImageUploadItem {
  id: string;             // Unique ID for this image item
  localUrl: string;       // base64 data URL for instant preview
  status: 'uploading' | 'success' | 'failed';
  file?: File;            // Original file for retry
  s3Url?: string;         // S3 URL after successful upload
  error?: string;         // Error message if failed
}

/**
 * ChatMessage extends ChatMessageResponse with UI-specific fields
 * All API response fields are available directly, plus UI fields below
 */
export interface ChatMessage extends ChatMessageResponse {
  text: string; // Combined text from blockContents
  user: SenderType; // Alias for senderType for UI compatibility
  isRead: boolean;
  time?: string; // Alias for createdAt for UI compatibility
  agentType?: ChatAgentType; // UI field for agent context
  _isHistory?: boolean; // Internal flag to track if message is from history
  isFEGeneratedId?: boolean; // true when messageId is FE-generated (not yet confirmed by BE)

  // Image upload fields
  uploadStatus?: 'uploading' | 'success' | 'failed';
  uploadError?: string;
  localFiles?: File[]; // Keep original files for retry
  imageUploadItems?: ImageUploadItem[]; // Per-image upload tracking
  attachedComponent?: import('./chat-component').ChatComponentItem | null;

  // Reply/Quote fields
  quotedMessageId?: string; // ID of the message being quoted
  quotedMessageText?: string; // Text preview of quoted message
  quotedMessageUser?: SenderType; // Who sent the quoted message
  isQueuing?: boolean; // Flag for messages waiting for connection
  isFollowUpDisabled?: boolean; // Flag to lock follow-up questions visually

  // Confirm-switch fields (when user tries to switch agent during countdown)
  confirmSwitchAgent?: ChatAgentType; // Set → this message is a confirm-switch prompt
  isConfirmDisabled?: boolean;        // true after user has answered (Có/Không)
  originalBlockContents?: import('./chat-message').BlockContent[]; // Order card data to re-add on "Có"
  msgUserImagesFw?: ForwardedImage[];
  isMsgHasBg?: boolean;
  isStopped?: boolean;
}

export interface AgentSwitchNotification {
  message: string;
  targetAgent: ChatAgentType;
  targetAgentName: string;
  question: string;
  answer?: string;
}

export interface QuickSaleState {
  isActive: boolean;
  orderInfo?: any;
  countdown?: number;
  isConfirming?: boolean;
}

export interface ChatHistory {
  agentType: ChatAgentType;
  conversationId: number;
  messages: ChatMessage[];
  lastUpdated: Date;
}

export interface ForwardMessage {
  fromAgent: ChatAgentType;
  toAgent: ChatAgentType;
  question: string;
  answer?: string;
  timestamp: Date;
}



export enum CommandType {
  BUSINESS = 'BUSINESS',
  REDIRECT = 'REDIRECT',
}